import { ReactNode, CSSProperties } from 'react';
import { Badge, Breadcrumb } from '@/components';
import './base-layout-style.scss';

interface BadgeStyles {
  backgroundColor?: string;
  color?: string;
  borderColor?: string;
}

interface HeaderBadge {
  text: string;
  styles?: BadgeStyles;
}

interface Header {
  headerText?: string;
  subHeaderText?: string;
  headerChildren?: ReactNode;
  headerBadges?: HeaderBadge[]; // Support for multiple badges
}

interface Breadcrumbs {
  showBreadcrumbs?: boolean;
  breadcrumbText?: string[]; // For dynamic segments (UUIDs) - replaces the segment value
}

interface BaseLayoutProps {
  breadcrumbs?: Breadcrumbs;
  header?: Header;
  children?: ReactNode;
  style?: CSSProperties;
}

/**
 * BaseLayout component is used to structure a page layout
 *
 * - `breadcrumbs`: Controls breadcrumb visibility and dynamic text. It has the following structure:
 *   - `showBreadcrumbs`: Controls whether breadcrumbs are shown. Defaults to true.
 *   - `breadcrumbText`: An array of breadcrumb texts, to replace dynamic route segments (UUIDs), passed by the parent component.
 *     Route names are automatically looked up from the route registry in `@/config/routes`.
 * - `header`: Controls header structure:
 *   - `headerText`: Main header text displayed at the top.
 *   - `headerChildren`: Additional content (e.g., action buttons or icons) that will be displayed in the top-right corner of the header.
 *   - `headerBadges`: An array of status badges displayed after the header text. Each badge is customizable with text and styles.
 * - `children`: The main content to be displayed within the layout.
 * - `isDashboardWidget`: Boolean variable to tell if the component is a dashboard widget
 * - `style`: This is an optional parameter to apply styling
 */
const BaseLayout = ({
  breadcrumbs = {
    showBreadcrumbs: true,
    breadcrumbText: [],
  },
  header,
  children,
  style = {},
}: BaseLayoutProps) => {
  const { showBreadcrumbs, breadcrumbText } = breadcrumbs || {};
  const { headerText, subHeaderText, headerChildren, headerBadges } =
    header || {};

  return (
    <div className={'base-card-container'}>
      {showBreadcrumbs && <Breadcrumb dynamicRouteTexts={breadcrumbText} />}
      {(headerText || headerChildren || headerBadges || subHeaderText) && (
        <div
          className={
            subHeaderText || headerBadges
              ? 'base-card-header-subheader'
              : 'base-card-header'
          }
        >
          {/* Top row */}
          <div className="base-card-header-top">
            <div className="base-card-heading">{headerText}</div>

            {headerChildren && (
              <div className="base-card-header-children">{headerChildren}</div>
            )}
          </div>

          {/* Bottom row */}
          {(headerBadges || subHeaderText) && (
            <div className="base-card-header-bottom">
              {subHeaderText && (
                <div className="base-card-subheading">{subHeaderText}</div>
              )}
              {headerBadges && headerBadges.length > 0 && (
                <div className="base-card-badges">
                  {headerBadges.map((badge, index) => (
                    <Badge key={index} text={badge.text} style={badge.styles} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="base-card-body" style={style}>
        {children}
      </div>
    </div>
  );
};

export { BaseLayout };
